// 2026-10-23 addition (clinician invite-only build, Piece 6) — a sales
// rep's own self-service page: invite a clinician (same email-only shape
// as the admin's own invite dialogs), and see the list of clinicians
// they've personally invited. Deliberately minimal — no reset-credentials,
// no change-email, no identity-claim visibility (decision 5: those stay
// admin-only, never exposed to a rep).
import React, { useContext, useEffect, useState } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, CircularProgress, Alert, Button, Chip, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import UserContext from "./UserContext";
import { formatEST } from "../dateUtils";
import ThemeToggle from "./ThemeToggle";
import { extractErrorDetail } from "./validators";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BRAND = { navy: "#00357a", gold: "#c9a96e" };

function InviteClinicianDialog({ open, onClose, onInvited, token, labBrandDisplay }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleClose = () => {
    if (submitting) return;
    setEmail(""); setError(null); setSuccess(null);
    onClose();
  };

  const canSubmit = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE}/sales/clinicians/invite`,
        { email: email.trim() }, { headers: { Authorization: `Bearer ${token}` } },
      );
      setSuccess({
        email: res.data?.email || email.trim(),
        emailSent: res.data?.email_sent !== false,
      });
      onInvited();
    } catch (err) {
      setError(extractErrorDetail(err, "Could not send this invite."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, color: BRAND.navy }}>Invite Clinician</DialogTitle>
      <DialogContent>
        {success ? (
          success.emailSent ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              Invite sent to <strong>{success.email}</strong>. They'll set their own password and complete
              their profile (name, practice, NPI, signature) on first login.
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Account created for <strong>{success.email}</strong>, but the invite email could not be
              sent. Contact an admin so they can resend their credentials.
            </Alert>
          )
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Email" type="email" value={email} fullWidth autoFocus
              helperText="They'll receive a temporary password and finish setting up their own account."
              onChange={(e) => setEmail(e.target.value)} />
            {/* 2026-10-23 addition (ui-ux-checker finding, lab-brand
                routing) — a rep has no other way to know which brand an
                invited clinician gets set up under (fixed server-side,
                never editable here) without this one-line note. */}
            {labBrandDisplay && (
              <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                This clinician will be set up under <strong>{labBrandDisplay}</strong>.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {success ? (
          <Button onClick={handleClose} variant="contained" sx={{ bgcolor: BRAND.navy }}>Done</Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
            <Button onClick={submit} variant="contained" disabled={submitting || !canSubmit} sx={{ bgcolor: BRAND.navy }}>
              {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Send Invite"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default function SalesRepDashboard() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { user, setUser } = useContext(UserContext);

  const [clinicians, setClinicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const fetchList = async () => {
      try {
        const res = await axios.get(`${API_BASE}/sales/my-invited-clinicians`, {
          headers: { Authorization: `Bearer ${user?.token}` },
        });
        setClinicians(res.data.clinicians || []);
      } catch (err) {
        if (err.response?.status === 401) { setUser(null); navigate("/", { replace: true }); return; }
        setError(extractErrorDetail(err, "Failed to load your invited clinicians."));
      } finally {
        setLoading(false);
      }
    };
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const handleLogout = () => {
    setUser(null);
    navigate("/", { replace: true });
  };

  const bg = isDark ? "#080d1a" : "#f4f7ff";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: bg }}>
      <Box sx={{ bgcolor: "rgba(0,14,36,0.96)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(201,169,110,0.2)" }}>
        <Box sx={{ height: 2, background: "linear-gradient(90deg, transparent, rgba(201,169,110,0.8) 30%, rgba(223,192,138,0.9) 50%, rgba(201,169,110,0.8) 70%, transparent)" }} />
        <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, py: 1, minHeight: 60,
          display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 1 }}>
          <img src="/logo-labarium-dark.png" alt="Labarium" style={{ height: 38, width: "auto" }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ThemeToggle />
            <Button size="small" onClick={handleLogout} sx={{ color: "rgba(255,255,255,0.7)", textTransform: "none" }}>
              Log out
            </Button>
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, md: 5 }, py: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", rowGap: 1 }}>
          <Box>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: "0.12em", color: BRAND.gold, mb: 0.5 }}>
              SALES REPRESENTATIVE
            </Typography>
            <Typography sx={{ fontSize: 26, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy }}>
              Your Invited Clinicians
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}
            sx={{ bgcolor: BRAND.navy, textTransform: "none", fontWeight: 700 }}>
            Add New Clinician
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: BRAND.navy }} /></Box>
        ) : clinicians.length === 0 && !error ? (
          <Alert severity="info">You haven't invited any clinicians yet. Use "Add New Clinician" to send your first invite.</Alert>
        ) : (
          <TableContainer sx={{ border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #E8E8E8", borderRadius: 2, overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#F7F7F8" }}>
                  {["Email", "Status", "Invited"].map((h) => (
                    <TableCell key={h} sx={{ fontSize: "0.63rem", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: isDark ? "#94a3b8" : "#555" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {clinicians.map((c) => (
                  <TableRow key={c.email} sx={{ "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#FAFAFA" } }}>
                    <TableCell sx={{ fontSize: "0.85rem", color: isDark ? "#e5e7eb" : "inherit" }}>{c.email}</TableCell>
                    <TableCell>
                      <Chip size="small"
                        label={c.identity_status === "pending_profile" ? "Pending Setup" : "Complete"}
                        color={c.identity_status === "pending_profile" ? "default" : "success"}
                        variant={c.identity_status === "pending_profile" ? "filled" : "outlined"} />
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>
                      {c.created_at ? formatEST(c.created_at, { dateOnly: true }) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* 2026-10-23 fix (ui-ux-checker finding) — onInvited must NOT close
          the dialog itself; the success/warning Alert needs to stay
          visible until the rep dismisses it via "Done". */}
      <InviteClinicianDialog open={dialogOpen} onClose={() => setDialogOpen(false)} token={user?.token}
        labBrandDisplay={user?.lab_brand_display}
        onInvited={() => setRefreshKey((k) => k + 1)} />
    </Box>
  );
}
